import { ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {} from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Subject, takeUntil, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { InfoTooltipComponent } from '../info-tooltip.component';

@Component({
  selector: 'formly-repeat-number',
  template: `
    <div class="my-2" [attr.data-cy]="field.props?.keyPath ?? null">
      @if (props.label && !props.hideLabel) {
        @if (props.labelType === undefined || props.labelType === 'titel') {
          <h3 class="font-bold mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </h3>
        } @else {
          <label class="block text-sm font-medium text-default mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </label>
        }
      }
      @if (props.description) {
        <p class="mb-2">{{ props.description }}</p>
      }
      @for (field of field.fieldGroup; track $index) {
        <div class="repeater-item">
          @if (props.repeatLabel) {
            <span class="text-default font-medium"> #{{ $index + 1 }} {{ props.repeatLabel }} </span>
          }
          <formly-field [field]="field"></formly-field>
        </div>
        @if (props.seperatorText && $index + 1 != model.length) {
          <h4 class="mt-2 mb-4 text-center text-default" [innerHTML]="props.seperatorText"></h4>
        }
      }
    </div>
  `,
  imports: [
    FormlyModule,
    FormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    InfoTooltipComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyRepeatNumberTypeComponent extends FieldArrayType implements OnInit, OnDestroy {
  formService = inject(FormService);
  modulesStore = inject(ModulesStore);

  linkedField: FormlyFieldConfig;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  constructor() {
    super();
  }

  ngOnInit() {
    const linkedFieldKey = this.props.linkedField.split('.').pop();

    if (linkedFieldKey) {
      this.validateValueAndCreateFields(this.form.get(linkedFieldKey).value);
    }

    this.form
      .get(linkedFieldKey)
      .valueChanges.pipe(
        takeUntil(this._unsubscribeAll),
        filter((value: any) => value > -1 && value !== null),
        tap(value => {
          this.validateValueAndCreateFields(value);
        })
      )
      .subscribe();
  }

  validateValueAndCreateFields(value) {
    if (value > 100) {
      const linkedFieldKey = this.props.linkedField.split('.').pop();
      this.form.get(linkedFieldKey).setValue(50);
      value = 100;
      this.props.description = 'Maximaal aantal herhalingsvelden (100) bereikt. ';
    } else {
      this.props.description = '';
    }

    this.addOrRemoveFields(
      value,
      this.form.get(this.field?.key?.toString())?.value ? this.form.get(this.field?.key?.toString())?.value : []
    );
  }

  addOrRemoveFields(value: number, currentValue: any[]) {
    const currentLength = currentValue.length;
    if (currentLength !== value) {
      if (currentLength < value) {
        // Calculate all default values once upfront instead of per field
        const defaultValues =
          this.field.fieldGroup.length > 0 || this.field.fieldArray?.['fieldGroup']?.length > 0
            ? this.formService.getDefaultValues([this.field])
            : this.props.defaultValue;

        // Calculate how many new fields we need to add
        const diff = value - currentLength;

        // Create array of all new field values at once
        const newFields = Array(diff)
          .fill(null)
          .map((_, index) => {
            return defaultValues;
          });

        // Add all fields in one go
        newFields.forEach(fieldValue => {
          this.add(this.field.fieldGroup.length, fieldValue);
        });

        // Set disabled state once for all new fields
        if (this.props.disabled) {
          const startIdx = this.field.fieldGroup.length - diff;
          for (let i = startIdx; i < this.field.fieldGroup.length; i++) {
            this.field.fieldGroup[i].props.disabled = this.props.disabled;
          }
        }
      } else {
        while (this.field.fieldGroup.length > value) {
          this.remove(this.field.fieldGroup.length - 1);
        }
      }
    }
    this.setDisabledState(this.field.formControl.disabled);
  }

  setDisabledState(disabled: boolean) {
    this.field.fieldGroup.forEach((field: FormlyFieldConfig) => {
      field.props.disabled = disabled;
    });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
